import './style.modules.scss'

import { SkeletonLoader } from '@ampcore/adel-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useAmpUserStatus } from '../../hooks/useAmpUserStatus'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { usePersonalizedHtml } from '../../hooks/usePersonalizedHtml'
import { rteContentFilter } from '../../lib/helpers/rteContentFilter'
import { MEDIA_QUERIES } from '../../theme/media-queries'
import { Link } from '../../types/Link.type'
import { UserStatus } from '../../types/UserStatus.enum'
import { RelatedInfo } from '../RelatedInfo/RelatedInfo'
import {
  RightRailCard,
  RightRailCardProps
} from '../RightRailCard/RightRailCard'
import { SocialShare } from '../SocialShare/SocialShare'
import { StickyMobileCta } from '../StickyMobileCta/StickyMobileCta'
import { StickyRightRail } from '../StickyRightRail/StickyRightRail'

export type ArticleRightRailProps = {
  socialShare?: {
    copyLink?: Omit<Link, 'url'>
    email?: Link
    linkedIn?: Link
    twitter?: Link
    facebook?: Link
  }
  relatedInfo?: {
    superHeadline?: string
    links?: Link[]
    analyticsName?: string
    personalizationId?: string
  }
  prospect?: RightRailCardProps
  client?: RightRailCardProps
  testId?: string
  className?: string
}

export const ArticleRightRail: React.FC<ArticleRightRailProps> = props => {
  const userStatus = useAmpUserStatus()
  const isLg = useMediaQuery(MEDIA_QUERIES.lg)

  const { socialShare, relatedInfo, prospect, client, testId, className } =
    props

  const portalEl =
    typeof document !== 'undefined'
      ? document.getElementById('article-right-rail-portal')
      : null

  const observerRef = useRef<IntersectionObserver | null>(null)
  const [isStickyCta, setIsStickyCta] = useState(false)

  const rightRailCardContent = useMemo<RightRailCardProps | undefined>(() => {
    if (
      client &&
      (userStatus === UserStatus.CLIENT_ACTIVE ||
        userStatus === UserStatus.CLIENT_EXPIRED)
    ) {
      return client
    }
    return prospect
  }, [userStatus, prospect, client])

  const { html: rightRailHtml, isLoading: isRightRailLoading } =
    usePersonalizedHtml(rightRailCardContent?.personalizationId)

  const { html: relatedInfoHtml, isLoading: isRelatedInfoLoading } =
    usePersonalizedHtml(relatedInfo?.personalizationId)

  const { personalizedCardHtml, personalizedStickyHtml } = useMemo(() => {
    if (!rightRailHtml) {
      return {
        personalizedCardHtml: null,
        personalizedStickyHtml: null
      }
    }

    const decoded = rteContentFilter(rightRailHtml)
    const wrapper = document.createElement('div')
    wrapper.innerHTML = decoded

    return {
      personalizedCardHtml:
        wrapper.querySelector('.RightRailCard')?.outerHTML ?? null,

      personalizedStickyHtml:
        wrapper.querySelector('.StickyMobileCta')?.outerHTML ?? null
    }
  }, [rightRailHtml])

  const personalizedRelatedInfoHtml = useMemo(() => {
    if (!relatedInfoHtml) {
      return null
    }

    const decoded = rteContentFilter(relatedInfoHtml)
    const wrapper = document.createElement('div')
    wrapper.innerHTML = decoded

    return wrapper.querySelector('.RelatedInfo')?.outerHTML ?? null
  }, [relatedInfoHtml])

  useEffect(() => {
    return () => observerRef.current?.disconnect()
  }, [])

  const stickyCtaButtonRef = (
    node: HTMLDivElement | HTMLAnchorElement | null
  ) => {
    observerRef.current?.disconnect()
    if (!node) return

    observerRef.current = new IntersectionObserver(entries => {
      const entry = entries[0]
      setIsStickyCta(!entry.isIntersecting && entry.boundingClientRect.top <= 0)
    })

    observerRef.current.observe(node)
  }

  const getCardSkeletonHeight = () => {
    const hasFeatureFlag = !!rightRailCardContent?.featureFlag?.label
    if (!isLg) {
      // Mobile/tablet card heights
      return hasFeatureFlag ? '220px' : '190px'
    }
    // Desktop card heights
    return hasFeatureFlag ? '310px' : '270px'
  }

  const renderRightRailContent = () => {
    if (rightRailCardContent?.personalizationId && isRightRailLoading) {
      return (
        <div className="ArticleRightRail-cardSkeleton u-marginBottom32">
          <SkeletonLoader width="100%" height={getCardSkeletonHeight()} />
        </div>
      )
    }

    if (personalizedCardHtml) {
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: personalizedCardHtml
          }}
        />
      )
    }

    if (rightRailCardContent) {
      return (
        <RightRailCard
          {...rightRailCardContent}
          className="u-block"
          testId={testId}
        />
      )
    }

    return null
  }

  const renderRelatedInfoContent = () => {
    if (relatedInfo?.personalizationId && isRelatedInfoLoading) {
      const linkCount = relatedInfo?.links?.length ?? 1
      const relatedInfoHeight = `${102 + linkCount * 33}px`
      return (
        <div className="ArticleRightRail-relatedInfoSkeleton u-marginBottom32">
          <SkeletonLoader width="100%" height={relatedInfoHeight} />
        </div>
      )
    }

    if (personalizedRelatedInfoHtml) {
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: personalizedRelatedInfoHtml
          }}
        />
      )
    }

    if (relatedInfo) {
      return <RelatedInfo className="u-marginBottom32" {...relatedInfo} />
    }

    return null
  }

  const renderMobileCard = () => {
    if (rightRailCardContent?.personalizationId && isRightRailLoading) {
      const skeleton = (
        <div ref={stickyCtaButtonRef}>
          <div className="ArticleRightRail-cardSkeleton u-marginBottom32">
            <SkeletonLoader width="100%" height={getCardSkeletonHeight()} />
          </div>
        </div>
      )
      return portalEl ? createPortal(skeleton, portalEl) : skeleton
    }

    if (personalizedCardHtml) {
      if (portalEl) {
        return createPortal(
          <div ref={stickyCtaButtonRef}>
            <div dangerouslySetInnerHTML={{ __html: personalizedCardHtml }} />
          </div>,
          portalEl
        )
      }

      return <div dangerouslySetInnerHTML={{ __html: personalizedCardHtml }} />
    }

    if (rightRailCardContent) {
      if (portalEl) {
        return createPortal(
          <div ref={stickyCtaButtonRef}>
            <RightRailCard
              {...rightRailCardContent}
              className="u-block"
              testId={testId}
            />
          </div>,
          portalEl
        )
      }

      return (
        <RightRailCard
          {...rightRailCardContent}
          className="u-block"
          testId={testId}
        />
      )
    }

    return null
  }

  return (
    <>
      <StickyRightRail className={`ArticleRightRail ${className || ''}`}>
        {isLg && renderRightRailContent()}

        {socialShare && (
          <SocialShare
            className="SocialShare--rightRail u-marginBottom32"
            {...socialShare}
          />
        )}

        {renderRelatedInfoContent()}
      </StickyRightRail>

      {!isLg && renderMobileCard()}

      {!isLg &&
        portalEl &&
        isStickyCta &&
        (personalizedStickyHtml ? (
          <div dangerouslySetInnerHTML={{ __html: personalizedStickyHtml }} />
        ) : (
          rightRailCardContent?.link && (
            <StickyMobileCta
              link={rightRailCardContent.link}
              testId={testId ? `${testId}-sticky-button` : undefined}
            />
          )
        ))}
    </>
  )
}
